// Frontend configuration utility for production builds

const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';
const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';
const isDebugMode = process.env.REACT_APP_DEBUG_MODE === 'true';

// Configuration object
const config = {
  // Environment
  environment: process.env.REACT_APP_ENVIRONMENT || 'development',
  isProduction,
  isDevelopment,
  isDebugMode: isDebugMode && !isProduction, // Force disable debug in production

  // API Configuration
  apiUrl: process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev',
  frontendUrl: process.env.REACT_APP_FRONTEND_URL || 'https://largetransfer.net',

  // Google Services
  googleAnalyticsId: process.env.REACT_APP_GOOGLE_ANALYTICS_ID,
  
  // File Upload Limits
  maxFileSize: parseInt(process.env.REACT_APP_MAX_FILE_SIZE) || 5368709120, // 5GB
  maxFilesPerUpload: parseInt(process.env.REACT_APP_MAX_FILES_PER_UPLOAD) || 1000, // Allow many files but enforce 5GB total size
  
  // Rate Limiting (Frontend enforcement)
  rateLimitUploads: parseInt(process.env.REACT_APP_RATE_LIMIT_UPLOADS) || 10,
  rateLimitWindow: parseInt(process.env.REACT_APP_RATE_LIMIT_WINDOW) || 900000,

  // Legal & Compliance
  privacyPolicyUrl: process.env.REACT_APP_PRIVACY_POLICY_URL || '/privacy-policy',
  termsOfServiceUrl: process.env.REACT_APP_TERMS_OF_SERVICE_URL || '/terms-of-service',
  contactEmail: process.env.REACT_APP_CONTACT_EMAIL || 'support@largetransfer.net',

  // Feature Flags
  features: {
    debugPanel: isDebugMode && !isProduction,
    googleAds: isProduction, // Only show ads in production
    googleAnalytics: isProduction && !!process.env.REACT_APP_GOOGLE_ANALYTICS_ID,
    errorTracking: isProduction,
    resizeObserverErrors: !isProduction // Only log ResizeObserver errors in development
  }
};

// Validation
const validateConfig = () => {
  const errors = [];

  // Production validations
  if (isProduction) {
    if (!config.googleAnalyticsId && config.features.googleAnalytics) {
      console.warn('⚠️  Google Analytics ID not set for production');
    }

    if (config.apiUrl.includes('localhost')) {
      errors.push('API URL should not include localhost in production');
    }

    if (config.frontendUrl.includes('localhost')) {
      errors.push('Frontend URL should not include localhost in production');
    }

    if (config.isDebugMode) {
      console.warn('⚠️  Debug mode is disabled in production builds');
    }
  }

  if (errors.length > 0) {
    console.error('❌ Configuration validation errors:');
    errors.forEach(error => console.error(`   - ${error}`));
    
    if (isProduction) {
      throw new Error(`Production build blocked: ${errors.join(', ')}`);
    }
  } else {
    console.log('✅ Frontend configuration validated');
  }

  return errors.length === 0;
};

// Logging utility (production-safe)
const logger = {
  debug: (...args) => {
    if (config.isDebugMode) {
      console.log('[DEBUG]', ...args);
    }
  },
  info: (...args) => {
    if (!isProduction || config.isDebugMode) {
      console.info('[INFO]', ...args);
    }
  },
  warn: (...args) => {
    console.warn('[WARN]', ...args);
  },
  error: (...args) => {
    console.error('[ERROR]', ...args);
    
    // In production, you might want to send to error tracking service
    if (isProduction && config.features.errorTracking) {
      // TODO: Integrate with Sentry or other error tracking
    }
  }
};

// Performance monitoring
const performance = {
  mark: (name) => {
    if (config.isDebugMode && window.performance && window.performance.mark) {
      window.performance.mark(name);
    }
  },
  measure: (name, startMark, endMark) => {
    if (config.isDebugMode && window.performance && window.performance.measure) {
      try {
        window.performance.measure(name, startMark, endMark);
        const measure = window.performance.getEntriesByName(name)[0];
        logger.debug(`Performance: ${name} took ${measure.duration.toFixed(2)}ms`);
      } catch (error) {
        logger.debug('Performance measurement failed:', error);
      }
    }
  }
};

// Initialize configuration
validateConfig();

// Log configuration in development
if (isDevelopment) {
  logger.debug('Configuration loaded:', {
    environment: config.environment,
    apiUrl: config.apiUrl,
    features: config.features,
    maxFileSize: `${(config.maxFileSize / 1024 / 1024 / 1024).toFixed(1)}GB`
  });
}

export default config;
export { logger, performance }; 